if (!customElements.get('enova-search-drawer')) {
  // Tarjetas visibles por tanda en los resultados (igual que sections/enova-search-results.liquid)
  const PAGE_SIZE = 24;

  class EnovaSearchDrawer extends HTMLElement {
    connectedCallback() {
      // Fuera del header sticky para no quedar atrapado en su contexto de apilamiento.
      if (this.parentElement !== document.body) {
        document.querySelectorAll(`body > #${this.id}`).forEach((old) => old.remove());
        document.body.appendChild(this);
        return;
      }

      this.input = this.querySelector('[data-enova-search-input]');
      this.back = this.querySelector('[data-enova-search-back]');
      this.results = this.querySelector('[data-enova-search-results]');
      this.suggestions = this.querySelector('[data-enova-search-suggestions]');
      this.allLink = this.querySelector('[data-enova-search-all]');
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.suggestUrl = this.dataset.suggestUrl || '/search/suggest';
      this.requestId = 0;

      this.abort = new AbortController();
      const signal = this.abort.signal;

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-enova-search-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      }, { signal });

      this.querySelectorAll('[data-enova-search-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), { signal });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      }, { signal });

      this.input.addEventListener('input', () => {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.search(this.input.value), 250);
      }, { signal });

      this.back?.addEventListener('click', (event) => this.clearSearch(event.detail === 0), { signal });

      this.prefetchTermCounts(signal);

      this.prefetchPopular(signal);

      // Al acercar el cursor a una búsqueda popular se piden sus tarjetas, para mostrarlas al
      // tocarla sin esperar
      this.querySelectorAll('[data-enova-search-term]').forEach((button) => {
        ['pointerenter', 'focus'].forEach((type) => {
          button.addEventListener(type, () => this.prefetchTermHtml(button.dataset.enovaSearchTerm), { passive: true, signal });
        });
      });

      // Búsquedas populares: igual que si se hubiera escrito el término, sin navegar.
      this.addEventListener('click', (event) => {
        const term = event.target.closest('[data-enova-search-term]');
        if (term) this.searchTerm(term.dataset.enovaSearchTerm);
        const more = event.target.closest('[data-enova-search-more]');
        if (more) this.loadMore(more, event.detail === 0);
      }, { signal });
    }

    /* Recuento de las búsquedas populares ------------------------------ */
    // El índice de búsqueda de Shopify devuelve listas distintas en pedidos seguidos (hasta en
    // la exacta), así que el número solo coincide con lo que se ve si ambos salen del mismo
    // pedido. Por eso cada término se precarga con su lista completa de resultados (la misma
    // búsqueda que al tocarlo: primero exacta, si no la normal): el número es el de esa lista y
    // al tocar el término se muestra esa lista, al instante. Se guarda 5 minutos en la sesión;
    // pasado ese plazo se muestra el guardado y se vuelve a pedir. Un término sin productos se
    // oculta.
    // Para que ya estén al abrir el panel se piden antes: con la página cargada y libre, o ante
    // la primera intención de abrir el buscador (mouse encima, toque o foco en un disparador).
    prefetchTermCounts(signal) {
      if (!this.querySelector('[data-enova-search-term]')) return;
      const load = () => this.loadTermCounts();

      document.addEventListener('pointerover', (event) => {
        if (!this.countsLoaded && event.target.closest?.('[data-enova-search-open]')) load();
      }, { passive: true, signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.countsLoaded && event.target.closest?.('[data-enova-search-open]')) load();
      }, { passive: true, signal });
      document.addEventListener('focusin', (event) => {
        if (!this.countsLoaded && event.target.closest?.('[data-enova-search-open]')) load();
      }, { signal });

      const whenIdle = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 3000 });
        else setTimeout(load, 1500);
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true, signal });
    }

    loadTermCounts() {
      if (this.countsLoaded) return;
      this.countsLoaded = true;

      const cache = this.readSnapshots();
      const now = Date.now();
      const queue = Array.from(this.querySelectorAll('[data-enova-search-term]')).filter((button) => {
        const cached = cache[button.dataset.enovaSearchTerm];
        if (!cached) return true;
        this.showTermCount(button, cached.count);
        return !this.isFresh(cached);
      });

      // Todos a la vez: son pocos términos.
      queue.forEach(async (button) => {
        const term = button.dataset.enovaSearchTerm;
        try {
          const snapshot = await this.fetchSnapshot(term);
          this.saveSnapshot(term, snapshot);
          this.showTermCount(button, snapshot.count);
        } catch (error) {
          // Sin lista el término se muestra igual, sin número, y al tocarlo busca normalmente.
        }
      });
    }

    // Resultados de una búsqueda tal como se mostrarían (primero exacta, si no la normal): su
    // número y la lista ordenada de productos, sin tarjetas (enova-search-count). Las tarjetas
    // se piden por esa lista al acercarse al término o al tocarlo.
    async fetchSnapshot(query) {
      const exactQuery = window.enovaExactTerms ? window.enovaExactTerms(query) : query;
      let section = null;
      if (exactQuery !== query) {
        section = await this.fetchSection(this.resultsUrl(exactQuery, 'enova-search-count'));
        if (this.sectionCount(section).total === 0) section = null;
      }
      if (!section) section = await this.fetchSection(this.resultsUrl(query, 'enova-search-count'));
      const { label } = this.sectionCount(section);
      return { count: label, handles: this.sectionHandles(section) };
    }

    sectionHandles(section) {
      const list = section?.querySelector('[data-handles]')?.dataset.handles || '';
      return list ? list.split(',') : [];
    }

    // data-count: productos distintos que llegaron (no el total estimado de Shopify);
    // data-capped: llegaron 250, el máximo por pedido.
    sectionCount(section) {
      const el = section?.querySelector('[data-count]');
      const total = Number(el?.dataset.count || 0);
      return { total, label: el?.dataset.capped === 'true' ? `${total}+` : total };
    }

    // Al terminar una búsqueda escrita que coincide con un término popular, el término pasa a
    // usar esa lista y su número (y sus tarjetas ya armadas quedan a mano para esta página).
    syncTermCount(query, snapshot) {
      const key = query.toLowerCase();
      const button = Array.from(this.querySelectorAll('[data-enova-search-term]'))
        .find((el) => el.dataset.enovaSearchTerm.trim().toLowerCase() === key);
      if (!button) return;
      const term = button.dataset.enovaSearchTerm;
      this.saveSnapshot(term, { count: snapshot.count, handles: snapshot.handles });
      const html = typeof snapshot.html === 'function' ? snapshot.html() : snapshot.html;
      if (html) this.termHtml().set(term, Promise.resolve(html));
      this.showTermCount(button, snapshot.count);
    }

    showTermCount(button, count) {
      // El campo de la cabecera (enova-header.js) muestra el mismo número en sus sugerencias.
      document.dispatchEvent(new CustomEvent('enova-search:term-count', {
        detail: { term: button.dataset.enovaSearchTerm, count },
      }));
      const item = button.closest('li');
      const block = button.closest('.enova-search__terms');
      if (count === 0) {
        if (item) item.hidden = true;
        if (block && !block.querySelector('li:not([hidden])')) block.hidden = true;
        return;
      }
      if (item) item.hidden = false;
      if (block) block.hidden = false;
      const label = button.querySelector('[data-enova-search-term-count]');
      if (label) label.textContent = count;
      button.setAttribute('aria-label', `${button.dataset.enovaSearchTerm}, ${count} ${count === 1 ? 'producto' : 'productos'}`);
    }

    // Guardado de la versión actual (número + lista) y de menos de 5 minutos
    isFresh(snapshot) {
      return Array.isArray(snapshot?.handles) && Date.now() - snapshot.at < 5 * 60 * 1000;
    }

    saveSnapshot(term, snapshot) {
      const cache = this.readSnapshots();
      cache[term] = { ...snapshot, at: Date.now() };
      this.writeSnapshots(cache);
    }

    readSnapshots() {
      try {
        return JSON.parse(sessionStorage.getItem('enova-search-terms')) || {};
      } catch (error) {
        return {};
      }
    }

    writeSnapshots(snapshots) {
      try {
        sessionStorage.setItem('enova-search-terms', JSON.stringify(snapshots));
      } catch (error) {
        // Sin espacio se guardan solo los números (al tocar el término se busca de nuevo); sin
        // sessionStorage (modo privado) se vuelve a pedir en la próxima página. Las listas son
        // cortas (solo identificadores), así que esto casi no debería pasar.
        try {
          const counts = {};
          Object.keys(snapshots).forEach((term) => {
            counts[term] = { count: snapshots[term].count, at: snapshots[term].at };
          });
          sessionStorage.setItem('enova-search-terms', JSON.stringify(counts));
        } catch (fallbackError) {
          // Nada que hacer.
        }
      }
    }

    // «Cargar más»: los siguientes 24 de la lista de esta búsqueda (data-handles), pedidos por
    // esa lista para que coincidan con el número. Se piden antes, cuando el botón se acerca a la
    // vista, así al tocarlo aparecen al instante.
    nextHandles() {
      const inner = this.results.querySelector('.enova-search__results-inner');
      const grid = inner?.querySelector('.enova-search__grid--results');
      if (!inner || !grid) return [];
      const all = (inner.dataset.handles || '').split(',').filter(Boolean);
      const shown = grid.querySelectorAll('.enova-search-card').length;
      return all.slice(shown, shown + PAGE_SIZE);
    }

    prefetchMore() {
      const handles = this.nextHandles();
      if (!handles.length) return null;
      const key = handles[0];
      if (this.morePrefetch?.key !== key) {
        const promise = this.fetchCards(handles);
        promise.catch(() => {
          if (this.morePrefetch?.promise === promise) this.morePrefetch = null;
        });
        this.morePrefetch = { key, promise };
      }
      return this.morePrefetch.promise;
    }

    watchMore() {
      const button = this.results.querySelector('[data-enova-search-more]');
      if (!button || !('IntersectionObserver' in window)) return;
      // Se desplaza la columna de resultados (escritorio) o el panel (móvil)
      const root = getComputedStyle(this.results).overflowY === 'auto' ? this.results : this.querySelector('.enova-search__main');
      this.moreObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.prefetchMore();
      }, { root, rootMargin: '0px 0px 600px 0px' });
      this.moreObserver.observe(button);
    }

    async loadMore(button, fromKeyboard) {
      const grid = this.results.querySelector('.enova-search__grid--results');
      if (!grid || button.getAttribute('aria-busy') === 'true') return;
      const id = this.requestId;
      button.setAttribute('aria-busy', 'true');
      let cards = [];
      try {
        cards = await this.prefetchMore();
      } catch (error) {
        cards = [];
      }
      button.removeAttribute('aria-busy');
      if (id !== this.requestId || !cards?.length) return;
      this.morePrefetch = null;
      cards.forEach((card) => card.classList.add('is-new'));
      grid.append(...cards);

      const wrap = button.closest('[data-enova-search-more-wrap]');
      const status = wrap?.querySelector('[data-enova-search-more-status]');
      if (!this.nextHandles().length) {
        this.moreObserver?.disconnect();
        wrap?.remove();
      } else if (status) {
        const shown = grid.querySelectorAll('.enova-search-card').length;
        status.textContent = `Mostrando ${shown} de ${status.dataset.total}`;
        // Si el botón sigue a la vista, el siguiente grupo se pide ya
        this.prefetchMore();
      }

      if (fromKeyboard) cards[0]?.querySelector('a')?.focus({ preventScroll: true });
    }

    // Las tarjetas del tema traen sus fotos diferidas y con prioridad baja (pensado para una
    // grilla larga). En el buscador las primeras filas se ven apenas aparecen: van primero.
    prioritizeImages(grid, count) {
      if (!grid) return;
      Array.from(grid.querySelectorAll('.enova-search-card')).slice(0, count).forEach((card) => {
        const img = card.querySelector('.m-product-card__main-image img');
        if (!img) return;
        img.loading = 'eager';
        img.fetchPriority = 'high';
      });
    }

    // Productos populares (sections/enova-search-popular.liquid): se piden con la página ya
    // cargada y libre, o antes ante la primera intención de abrir el buscador, y entran al panel
    // en cuanto llegan. Si llegan con el panel ya abierto, suben con la misma entrada que
    // «Cargar más».
    prefetchPopular(signal) {
      const grid = this.querySelector('[data-enova-search-popular]');
      if (!grid?.dataset.url) return;
      const load = () => this.loadPopular();
      document.querySelectorAll('[data-enova-search-open]').forEach((trigger) => {
        ['pointerenter', 'focus', 'touchstart'].forEach((type) => {
          trigger.addEventListener(type, load, { passive: true, once: true, signal });
        });
      });
      const whenIdle = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 3000 });
        else setTimeout(load, 1500);
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true, signal });
    }

    loadPopular() {
      const grid = this.querySelector('[data-enova-search-popular]');
      if (!grid?.dataset.url || this.popularPromise) return this.popularPromise;
      const limit = parseInt(grid.dataset.limit, 10) || 12;
      this.popularPromise = fetch(grid.dataset.url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cards = Array.from(doc.querySelectorAll('[data-enova-search-popular-cards] > .enova-search-card')).slice(0, limit);
          const late = this.classList.contains('is-open') && !this.classList.contains('is-intro');
          if (late) cards.forEach((card) => card.classList.add('is-new'));
          grid.append(...cards);
          this.prioritizeImages(grid, 6);
        })
        .catch(() => {
          this.popularPromise = null;
        });
      return this.popularPromise;
    }

    // Vuelve a la vista inicial (búsquedas y productos populares) sin cerrar el panel.
    clearSearch(fromKeyboard) {
      clearTimeout(this.debounce);
      this.requestId += 1;
      this.setInputValue('');
      this.resetResults(true);
      // Con teclado o mouse el foco pasa al campo para escribir de nuevo; en táctil no, para
      // que el teclado no tape la vista.
      if (fromKeyboard || window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.input.focus({ preventScroll: true });
      }
    }

    searchTerm(term) {
      clearTimeout(this.debounce);
      // En táctil no se enfoca el campo: el teclado taparía los resultados.
      const focus = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (focus) this.input.focus({ preventScroll: true });
      // La flecha entra ya con el término, en un cuadro liviano y a la par de su texto (sin
      // esperar a los resultados como al escribir, donde movería el texto mientras se teclea).
      this.showBack();
      this.setInputValue(term, () => {
        if (focus) this.input.setSelectionRange(term.length, term.length);
      });
      const snapshot = this.readSnapshots()[term];
      if (this.isFresh(snapshot)) this.showSnapshot(term, snapshot);
      else this.search(term);
    }

    // Término popular ya contado: se muestra su lista (la del número que se ve). Sus tarjetas se
    // piden por esa lista; si se acercó el cursor antes, ya están y aparecen sin esperar. Las
    // búsquedas relacionadas no afectan al recuento y se piden al momento.
    async showSnapshot(term, snapshot) {
      const id = ++this.requestId;
      this.fetchAbort?.abort();
      this.fetchAbort = new AbortController();
      const { signal } = this.fetchAbort;
      this.cancelClear();
      this.classList.add('is-searching', 'is-loading');

      // Las relacionadas anteriores quedan atenuadas hasta que llegan las nuevas
      this.fetchSection(this.suggestionsUrl(term), signal)
        .then((section) => {
          if (id === this.requestId) this.suggestions.innerHTML = section ? section.innerHTML.trim() : '';
        })
        .catch((error) => {
          if (error?.name !== 'AbortError' && id === this.requestId) this.suggestions.innerHTML = '';
        });

      try {
        const html = await this.prefetchTermHtml(term, snapshot);
        if (id !== this.requestId) return;
        this.renderResults(term, html, snapshot.count);
      } catch (error) {
        if (id === this.requestId) this.search(term);
        return;
      }
      if (id === this.requestId) this.markResultsReady();
    }

    // Tarjetas de un término contado (las primeras 24 de su lista), una sola vez por página
    termHtml() {
      if (!this.termHtmlCache) this.termHtmlCache = new Map();
      return this.termHtmlCache;
    }

    prefetchTermHtml(term, snapshot = this.readSnapshots()[term]) {
      const cache = this.termHtml();
      if (cache.has(term)) return cache.get(term);
      if (!this.isFresh(snapshot)) return Promise.reject(new Error('sin lista'));
      const promise = this.fetchCards(snapshot.handles.slice(0, PAGE_SIZE))
        .then((cards) => this.resultsMarkup(term, snapshot.count, snapshot.handles, cards));
      cache.set(term, promise);
      promise.catch(() => cache.delete(term));
      return promise;
    }

    // Mismo marcado que sections/enova-search-results.liquid
    resultsMarkup(query, label, handles, cards) {
      const total = parseInt(label, 10) || 0;
      const inner = document.createElement('div');
      inner.className = 'enova-search__results-inner';
      inner.dataset.count = String(total);
      inner.dataset.capped = String(String(label).includes('+'));
      inner.dataset.handles = handles.join(',');
      if (!total) return inner.outerHTML;
      const heading = document.createElement('p');
      heading.className = 'enova-search__heading';
      heading.innerHTML = `Resultados <span class="enova-search__heading-count">(${label})</span>`;
      const grid = document.createElement('div');
      grid.className = 'enova-search__grid enova-search__grid--results';
      grid.append(...cards);
      inner.append(heading, grid);
      if (total > PAGE_SIZE) {
        const more = document.createElement('div');
        more.className = 'enova-search__more';
        more.dataset.enovaSearchMoreWrap = '';
        more.innerHTML = `<p class="enova-search__more-status" data-enova-search-more-status data-total="${label}">Mostrando ${cards.length} de ${label}</p><button type="button" class="enova-search__more-button" data-enova-search-more>Cargar más</button>`;
        inner.append(more);
      }
      return inner.outerHTML;
    }

    // Tarjetas por lista de productos, en su orden: pedidos de a 12, en paralelo
    fetchCards(handles, signal) {
      const chunks = [];
      for (let i = 0; i < handles.length; i += 12) chunks.push(handles.slice(i, i + 12));
      return Promise.all(chunks.map((chunk) => this.fetchSection(this.cardsUrl(chunk), signal)))
        .then((sections) => sections.flatMap((section) => (
          section ? Array.from(section.querySelectorAll('[data-enova-search-cards] > .enova-search-card')) : []
        )));
    }

    cardsUrl(handles) {
      const params = new URLSearchParams({
        q: `enova-cards:${handles.join(',')}`,
        type: 'product',
        section_id: 'enova-search-cards',
      });
      return `${this.searchUrl}?${params}`;
    }

    // content: HTML en texto o la sección ya interpretada (se mueven sus nodos)
    renderResults(query, content, label) {
      this.moreObserver?.disconnect();
      this.morePrefetch = null;
      if (content instanceof Element) this.results.replaceChildren(...content.childNodes);
      else this.results.innerHTML = content || '';
      // Lista nueva sobre otra ya a la vista: arriba de todo (el cambio de contenido disimula el
      // salto). La primera sube al aparecer (markResultsReady).
      if (this.classList.contains('has-results')) this.scrollToTop();
      this.prioritizeImages(this.results.querySelector('.enova-search__grid--results'), 8);
      this.watchMore();
      if (parseInt(label, 10) > 0) {
        const params = new URLSearchParams({ q: query, 'options[prefix]': 'last' });
        this.allLink.href = `${this.searchUrl}?${params}`;
        this.setAllLabel(`Ver todos los resultados (${label})`);
      } else {
        this.allLink.href = this.allLink.dataset.defaultHref;
        this.setAllLabel(this.allLink.dataset.defaultLabel);
      }
    }

    // El texto del botón inferior cambia con un fundido corto en vez de saltar
    setAllLabel(text) {
      if (this.allLink.textContent === text) return;
      this.allLink.textContent = text;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.allLink.animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 300, easing: 'ease' });
    }

    // Primera respuesta: la vista inicial se apaga (.is-leaving) y recién entonces entran las
    // búsquedas relacionadas y los resultados, por filas (CSS). Así no se pisan dos vistas ni las
    // transiciones arrancan en el mismo cuadro pesado en que se insertaron las tarjetas. La
    // flecha de volver aparece con ellos (al tocar un término popular ya entró antes).
    // Con resultados ya a la vista, la lista nueva solo entra en su lugar.
    markResultsReady() {
      this.classList.remove('is-loading');
      if (!this.classList.contains('is-searching')) return;
      if (this.classList.contains('has-results')) {
        this.showBack();
        return;
      }
      const reveal = () => {
        clearTimeout(this.revealTimer);
        this.classList.remove('is-leaving');
        this.classList.add('has-results');
        this.showBack();
        this.scrollToTop();
      };
      if (this.reducedMotion() || !this.classList.contains('is-open')) {
        reveal();
        return;
      }
      this.classList.add('is-leaving');
      clearTimeout(this.revealTimer);
      this.revealTimer = setTimeout(() => {
        if (this.classList.contains('is-leaving')) reveal();
      }, 200);
    }

    showBack() {
      this.classList.add('has-back');
      this.back?.removeAttribute('tabindex');
    }

    hideBack() {
      this.classList.remove('has-back');
      this.back?.setAttribute('tabindex', '-1');
    }

    scrollToTop() {
      this.querySelector('.enova-search__main')?.scrollTo({ top: 0 });
      this.results.scrollTo?.({ top: 0 });
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Cambia el texto del campo sin salto: el anterior (o el placeholder) se apaga y el nuevo
    // entra enfocándose. onSet corre apenas el texto nuevo está puesto.
    setInputValue(value, onSet) {
      const input = this.input;
      const apply = () => {
        input.value = value;
        onSet?.();
      };
      this.inputFade?.cancel();
      this.inputFade = null;
      if (this.reducedMotion() || input.value === value || !input.animate) {
        apply();
        return;
      }
      const fadeOut = input.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'ease-in', fill: 'forwards' });
      this.inputFade = fadeOut;
      fadeOut.onfinish = () => {
        if (this.inputFade !== fadeOut) return;
        apply();
        this.inputFade = input.animate(
          [{ opacity: 0, filter: 'blur(3px)' }, { opacity: 1, filter: 'blur(0)' }],
          { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        );
        fadeOut.cancel();
      };
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.fetchAbort?.abort();
      if (this.classList.contains('is-open') || this.classList.contains('is-closing')) {
        this.unlockPage();
      }
    }

    open(trigger) {
      this.clearCloseWait();
      this.classList.remove('is-closing');
      // Primero se cierran menú y paneles del header, que liberan el bloqueo de scroll al cerrarse.
      document.dispatchEvent(new CustomEvent('enova-search:open'));
      this.markSliding();
      this.classList.add('is-open');
      document.documentElement.classList.add('enova-search-open');
      this.lockPage();
      this.loadTermCounts();
      this.loadPopular();
      this.playIntro();
      document.querySelectorAll('[data-enova-search-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      setTimeout(() => this.input.focus({ preventScroll: true }), 50);
    }

    close() {
      if (!this.classList.contains('is-open') || this.classList.contains('is-closing')) return;

      this.markSliding();
      this.classList.add('is-closing');
      this.classList.remove('is-open');
      document.documentElement.classList.remove('enova-search-open');
      document.querySelectorAll('[data-enova-search-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.finishClose();
        return;
      }

      const panel = this.querySelector('.enova-search__panel');
      const onEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', onEnd);
        this.finishClose();
      };
      panel.addEventListener('transitionend', onEnd);
      this.closeTimer = setTimeout(() => {
        panel.removeEventListener('transitionend', onEnd);
        this.finishClose();
      }, 450);
    }

    // Secuencia de entrada de términos y productos (CSS .is-intro): solo en la primera apertura
    // de la sesión. Se quita al terminar para que no se repita al volver de una búsqueda
    // (el bloque popular pasa de display none a visible y reiniciaría las animaciones).
    playIntro() {
      if (this.introPlayed) return;
      this.introPlayed = true;
      try {
        if (sessionStorage.getItem('enova-search-intro')) return;
        sessionStorage.setItem('enova-search-intro', '1');
      } catch (error) {
        // Sin sessionStorage la intro se ve una vez por página.
      }
      this.classList.add('is-intro');
      clearTimeout(this.introTimer);
      this.introTimer = setTimeout(() => this.classList.remove('is-intro'), 1600);
    }

    // Capa de GPU para el panel solo mientras entra o sale (igual que el mini-cart).
    markSliding() {
      clearTimeout(this.slideTimer);
      this.classList.add('is-sliding');
      this.slideTimer = setTimeout(() => this.classList.remove('is-sliding'), 500);
    }

    finishClose() {
      if (!this.classList.contains('is-closing')) return;
      this.clearCloseWait();
      this.classList.remove('is-closing', 'is-intro', 'is-return');
      this.unlockPage();
      // El foco no vuelve al campo de la cabecera: se quita, sin estilos de foco a la vista.
      if (this.contains(document.activeElement)) document.activeElement.blur();
    }

    // enova-search-lock: el bloqueo propio del buscador (enova-search.css)
    lockPage() {
      document.documentElement.classList.add('prevent-scroll', 'enova-search-lock');
    }

    unlockPage() {
      document.documentElement.classList.remove('prevent-scroll', 'enova-search-lock', 'enova-search-open');
    }

    clearCloseWait() {
      if (!this.closeTimer) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    // Vuelta a la vista inicial. Con resultados a la vista (animate), primero se apagan
    // (.is-clearing) mientras la flecha sale; recién entonces se vacían y los populares vuelven
    // por filas (.is-return), igual que entraron los resultados.
    resetResults(animate = false) {
      this.fetchAbort?.abort();
      clearTimeout(this.revealTimer);
      this.hideBack();
      this.allLink.href = this.allLink.dataset.defaultHref;
      this.setAllLabel(this.allLink.dataset.defaultLabel);
      if (animate && this.classList.contains('has-results') && !this.reducedMotion()) {
        this.classList.remove('is-loading');
        this.classList.add('is-clearing');
        clearTimeout(this.clearTimer);
        this.clearTimer = setTimeout(() => {
          this.clearTimer = null;
          this.finishReset(true);
        }, 220);
        return;
      }
      this.cancelClear();
      this.finishReset(false);
    }

    finishReset(animate) {
      const returning = animate && this.classList.contains('has-results');
      this.moreObserver?.disconnect();
      this.classList.remove('is-searching', 'is-loading', 'has-results', 'is-leaving', 'is-clearing');
      this.results.innerHTML = '';
      this.suggestions.innerHTML = '';
      this.scrollToTop();
      if (!returning) return;
      this.classList.add('is-return');
      clearTimeout(this.returnTimer);
      this.returnTimer = setTimeout(() => this.classList.remove('is-return'), 1000);
    }

    // Se vuelve a buscar mientras se apagaban los resultados: siguen donde estaban (atenuados
    // como cualquier lista que espera la siguiente) en vez de vaciarse y volver a entrar.
    cancelClear() {
      if (!this.clearTimer) return;
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
      this.classList.remove('is-clearing');
    }

    resultsUrl(term, sectionId = 'enova-search-results') {
      const params = new URLSearchParams({
        q: term,
        type: 'product',
        'options[unavailable_products]': 'last',
        'options[prefix]': 'last',
        section_id: sectionId,
      });
      return `${this.searchUrl}?${params}`;
    }

    suggestionsUrl(term) {
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'collection,query',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        section_id: 'enova-search-suggestions',
      });
      return `${this.suggestUrl}?${params}`;
    }

    fetchText(url, signal) {
      return fetch(url, { signal }).then((response) => (response.ok ? response.text() : Promise.reject(response.status)));
    }

    parseSection(html) {
      return html ? new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section') : null;
    }

    fetchSection(url, signal) {
      return this.fetchText(url, signal).then((html) => this.parseSection(html));
    }

    // Igual que snippets/enova-search.liquid: primero coincidencias exactas (cada palabra entre
    // comillas); si no hay ninguna, la búsqueda normal de Shopify.
    async search(rawQuery) {
      const query = rawQuery.trim();
      if (!query) {
        this.requestId += 1;
        this.resetResults(true);
        return;
      }

      const id = ++this.requestId;
      this.fetchAbort?.abort();
      this.fetchAbort = new AbortController();
      const { signal } = this.fetchAbort;
      this.cancelClear();
      this.classList.add('is-searching', 'is-loading');

      const exactQuery = window.enovaExactTerms ? window.enovaExactTerms(query) : query;

      try {
        // Las dos listas llegan como texto: el recuento se lee del texto y solo se interpreta la
        // que se va a mostrar (interpretar las dos era una tarea larga que trababa la entrada)
        const [normalText, exactText, suggestions] = await Promise.all([
          this.fetchText(this.resultsUrl(query), signal),
          exactQuery !== query ? this.fetchText(this.resultsUrl(exactQuery), signal).catch(() => null) : null,
          this.fetchSection(this.suggestionsUrl(query), signal).catch(() => null),
        ]);
        if (id !== this.requestId) return;

        const exactCount = exactText ? Number((exactText.match(/data-count="(\d+)"/) || [])[1] || 0) : 0;
        const chosen = this.parseSection(exactCount > 0 ? exactText : normalText);
        const snapshot = {
          count: this.sectionCount(chosen).label,
          handles: this.sectionHandles(chosen),
          // Solo se serializa si la búsqueda coincide con un término popular (syncTermCount)
          html: () => (chosen ? chosen.innerHTML : ''),
        };
        this.syncTermCount(query, snapshot);
        // Los nodos ya interpretados pasan tal cual a la columna (sin volver a leer el HTML)
        this.renderResults(query, chosen, snapshot.count);
        this.suggestions.innerHTML = suggestions ? suggestions.innerHTML.trim() : '';
      } catch (error) {
        if (error?.name === 'AbortError') return;
      } finally {
        if (id === this.requestId) this.markResultsReady();
      }
    }
  }

  customElements.define('enova-search-drawer', EnovaSearchDrawer);
}
